import React, { useContext, useState } from 'react';
import { Link, useLocation, useOutletContext } from 'react-router-dom';
import classNames from 'classnames';
// @ts-ignore
import permissions from 'invitemember-permissions-model';
import Dropdown from '@/components/Dropdown';
import ThreeDotsVertical from '@/images/icons/ThreeDotsVertical';
import TransferModal from '@/pages/settings/EditProject/TransferModal';
import DeleteModal from '@/pages/settings/EditProject/DeleteModal';
import { OutletContext, Project } from '@/types';
import { DropdownContext } from '@/components/Dropdown/DropdownContext';
import { fetchAPI } from '@/api';
import { useHasPermission } from '@/hooks/useHasPermission';

function EditProjectHeader({ project } : { project: Project }) {
	const location = useLocation();
	const info = "/settings/edit-project/info";
	const team = "/settings/edit-project/team";

	const hasTeamPermissions = useHasPermission(permissions.MENU_MANAGE_TEAM);
	const hasTransferPermissions = useHasPermission(permissions.TRANSFER_PROJECT);
	const hasDeletePermissions = useHasPermission(permissions.DELETE_PROJECT);
	const hasUserPermissions = useHasPermission(permissions.USER_DATA);

	const { auth } = useOutletContext<OutletContext>();
	const { openDropdownId } = useContext(DropdownContext);
	const isDropdownOpen = openDropdownId === 'edit-project-dropdown';

	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [deleteModalMessage, setDeleteModalMessage] = useState(null);
	const [deleteModalIsConfirmed, setDeleteModalIsConfirmed] = useState(false);
	const [deleteModalError, setDeleteModalError] = useState(null);

	const [transferModalOpen, setTransferModalOpen] = useState(false);
	const [transferModalMessage, setTransferModalMessage] = useState(null);
	const [transferModalIsConfirmed, setTransferModalIsConfirmed] = useState(false);
	const [transferModalError, setTransferModalError] = useState(null);

	const [isCsvLoading, setIsCsvLoading] = useState(false);
	const [csvError, setCsvError] = useState<string | null>(null);
	// const [isCsvLoading, setIsCsvLoading] = useState(false);

	const onRequestCsv = async () => {
		try {
			setIsCsvLoading(true);

			const response = await fetchAPI("/project/export-data-csv", auth.user.token, {
				projectId: project.projectId,
			});

			if (!response.ok) {
				throw new Error(`This is an HTTP error: The status is ${response.status}`);
			}

			// Get content type to check if it's a CSV or ZIP
			const contentType = response.headers.get('Content-Type');
			const contentDisposition = response.headers.get('Content-Disposition');
			const filename = contentDisposition?.split('"')[1] || 'exported_data';

			if (contentType?.startsWith('text/csv')) {
				const csvText = await response.text();
				const blob = new Blob([csvText], { type: 'text/csv' });
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', `${filename}.csv`);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} else if (contentType?.startsWith('application/zip')) {
				const blob = await response.blob();
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', `${filename}.zip`);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} else {
				console.error('Unsupported content type:', contentType);
			}

			setCsvError(null);
		} catch (error: any) {
			let errorMessage = 'An unexpected error occurred';
			if (error.response?.message) {
				errorMessage = error.response.message;
			}
			setCsvError(errorMessage);
			alert(errorMessage);
		} finally {
			setIsCsvLoading(false);
		}
	};


	return (
		<>
			<div className="grid grid-cols-[2fr_1fr] mb-10 md:mb-0">
				<h1 className="h1 mb-0 md:mb-10">Edit project</h1>
				<div className="flex justify-end items-start col-start-2 row-start-1">
					{(hasTransferPermissions || hasUserPermissions || hasDeletePermissions) && (
						<Dropdown
							id="edit-project-dropdown"
							button={
								<button className={classNames(
									'btn-secondary w-8 h-8 outline-0',
									isDropdownOpen && 'bg-[#DFD8CD] border-[#DFD8CD]',
								)}>
									<ThreeDotsVertical />
								</button>
							}
							className="relative inline-flex mt-1"
							top="top-[calc(100%+10px)]"
						>
							{hasTransferPermissions && (
								<button
									className="flex items-center py-2.5 px-4 w-full text-white text-sm font-medium border-b border-b-[#313334] last:border-b-0 first:rounded-t-xl last:rounded-b-xl hover:bg-[#313334] transition"
									onClick={() => setTransferModalOpen(true)}
								>
									Transfer project
								</button>
							)}
							{hasUserPermissions && (
								<button
									className="flex items-center py-2.5 px-4 w-full text-white text-sm font-medium border-b border-b-[#313334] last:border-b-0 first:rounded-t-xl last:rounded-b-xl hover:bg-[#313334] transition"
									onClick={() => onRequestCsv()}
								>
									Export data (CSV)
								</button>
							)}
							{hasDeletePermissions && (
								<button
									className="flex items-center py-2.5 px-4 w-full text-white text-sm font-medium border-b border-b-[#313334] last:border-b-0 first:rounded-t-xl last:rounded-b-xl hover:bg-[#313334] transition"
									onClick={() => setDeleteModalOpen(true)}
								>
									Delete project
								</button>
							)}
						</Dropdown>
					)}
				</div>
			</div>
			<div className="flex gap-12 mb-10 border-b border-b-[#DBDBDB]">
				<Link to={info} className={classNames(
					`pb-3 relative before:content-[''] before:absolute before:top-auto before:left-[14px] before:bottom-[-1px] before:right-[13px] before:bg-brand-green before:h-[2px] before:rounded-full before:transition-opacity before:duration-150 hover:text-brand-green transition duration-150`,
					location.pathname !== info && 'before:opacity-0 text-[#A5A5A5]'
				)}>Main information
				</Link>
				{hasTeamPermissions && (
					<Link to={team} className={classNames(
						`pb-3 relative before:content-[''] before:absolute before:top-auto before:left-[14px] before:bottom-[-1px] before:right-[13px] before:bg-brand-green before:h-[2px] before:rounded-full before:transition-opacity before:duration-150 hover:text-brand-green transition duration-150`,
						location.pathname !== team && 'before:opacity-0 text-[#A5A5A5]'
					)}>Manage team
					</Link>
				)}
			</div>

			{hasTransferPermissions && (
				<TransferModal
					modalOpen={transferModalOpen}
					setModalOpen={setTransferModalOpen}
					modalMessage={transferModalMessage}
					modalError={transferModalError}
					setModalMessage={setTransferModalMessage}
					setModalError={setTransferModalError}
					isConfirmed={transferModalIsConfirmed}
					setIsConfirmed={setTransferModalIsConfirmed}
					projectId={project.projectId}
				/>
			)}
			{hasDeletePermissions && (
				<DeleteModal
					modalOpen={deleteModalOpen}
					setModalOpen={setDeleteModalOpen}
					modalMessage={deleteModalMessage}
					modalError={deleteModalError}
					setModalMessage={setDeleteModalMessage}
					setModalError={setDeleteModalError}
					isConfirmed={deleteModalIsConfirmed}
					setIsConfirmed={setDeleteModalIsConfirmed}
					projectId={project.projectId}
				/>
			)}
		</>
	);
}

export default EditProjectHeader;
